import { useState, useEffect } from 'react';
import { X, Clock, Save, AlertCircle, Plus, CalendarDays } from 'lucide-react';
import { client } from '@/api';

const WeeklyAvailabilityModal = ({ isOpen, onClose, astrologerId, onSuccess }) => {
    const [weeklySchedule, setWeeklySchedule] = useState({
        sunday: { enabled: false, slots: [{ startTime: '9:00 AM', endTime: '5:00 PM' }] },
        monday: { enabled: true, slots: [{ startTime: '9:00 AM', endTime: '5:00 PM' }] },
        tuesday: { enabled: true, slots: [{ startTime: '9:00 AM', endTime: '5:00 PM' }] },
        wednesday: { enabled: true, slots: [{ startTime: '9:00 AM', endTime: '5:00 PM' }] },
        thursday: { enabled: true, slots: [{ startTime: '9:00 AM', endTime: '5:00 PM' }] },
        friday: { enabled: true, slots: [{ startTime: '9:00 AM', endTime: '5:00 PM' }] },
        saturday: { enabled: false, slots: [{ startTime: '9:00 AM', endTime: '5:00 PM' }] },
    });
    const [specificDates, setSpecificDates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const daysOfWeek = [
        'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
    ];

    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 6; hour <= 23; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const hour12 = hour > 12 ? hour - 12 : hour;
                const period = hour < 12 ? 'AM' : 'PM';
                const timeStr = `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
                options.push(timeStr);
            }
        }
        return options;
    };
    const timeOptions = generateTimeOptions();

    useEffect(() => {
        if (isOpen && astrologerId) {
            fetchCurrentWeeklyAvailability();
        }
    }, [isOpen, astrologerId]);

    const fetchCurrentWeeklyAvailability = async () => {
        try {
            const { data } = await client.get('/api/meeting/weekly-availability', {
                params: { astrologerId }
            });

            if (data.success && data.data.weeklySchedule) {
                setWeeklySchedule(data.data.weeklySchedule);
            }
            if (data.success && data.data.specificDates) {
                const formattedDates = data.data.specificDates.map(dateObj => ({
                    ...dateObj,
                    date: new Date(dateObj.date).toISOString().split('T')[0]
                }));
                setSpecificDates(formattedDates);
            }
        } catch (error) {
            console.error('Error fetching weekly availability:', error);
        }
    };

    const handleDayToggle = (day) => {
        setWeeklySchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                enabled: !prev[day].enabled
            }
        }));
    };

    const addSlot = (day) => {
        setWeeklySchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                slots: [...prev[day].slots, { startTime: '9:00 AM', endTime: '5:00 PM' }]
            }
        }));
    };

    const removeSlot = (day, index) => {
        setWeeklySchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                slots: prev[day].slots.filter((_, i) => i !== index)
            }
        }));
    };

    const updateSlot = (day, index, field, value) => {
        setWeeklySchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                slots: prev[day].slots.map((slot, i) =>
                    i === index ? { ...slot, [field]: value } : slot
                )
            }
        }));
    };

    const addSpecificDate = () => {
        const today = new Date();
        const newDate = {
            id: Date.now(),
            date: today.toISOString().split('T')[0],
            type: 'unavailable',
            startTime: '9:00 AM',
            endTime: '5:00 PM'
        };
        setSpecificDates(prev => [...prev, newDate]);
    };

    const removeSpecificDate = (id) => {
        setSpecificDates(prev => prev.filter(date => date.id !== id));
    };

    const updateSpecificDate = (id, field, value) => {
        setSpecificDates(prev => prev.map(date =>
            date.id === id ? { ...date, [field]: value } : date
        ));
    };

    const handleSubmit = async () => {
        setError('');
        setLoading(true);

        try {
            const { data } = await client.post('/api/meeting/set-weekly-availability', {
                astrologerId,
                weeklySchedule,
                specificDates: specificDates.map(date => ({
                    date: date.date,
                    type: date.type,
                    startTime: date.type === 'available' ? date.startTime : null,
                    endTime: date.type === 'available' ? date.endTime : null
                }))
            });

            if (data.success) {
                onSuccess?.(data.data);
                onClose();
                setError('');
                window.location.reload();
            } else {
                setError(data.message || 'Failed to set weekly availability');
            }
        } catch (error) {
            setError('Network error. Please try again.');
            console.error('Error setting weekly availability:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-100">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-orange-50 to-white">
                    <div className="flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-orange-500" />
                        <h2 className="text-lg font-semibold text-gray-900">Set Weekly Availability</h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6 space-y-8">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600">{error}</span>
                        </div>
                    )}

                    <div className="space-y-4">
                        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                            <Clock className="h-4 w-4 text-orange-500" /> Work Hours
                        </h3>
                        <p className='text-xs text-red-500'>*Note: This will set the schedule for the next 7 days starting from today.</p>
                        <p className="text-xs text-gray-500">Set time ranges for each working day.</p>

                        <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                            {daysOfWeek.map((day) => (
                                <div key={day} className="py-3 px-4 bg-white hover:bg-gray-50 transition">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <input
                                            type="checkbox"
                                            checked={weeklySchedule[day].enabled}
                                            onChange={() => handleDayToggle(day)}
                                            className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                                        />
                                        <label className="text-sm font-medium capitalize">{day}</label>
                                    </div>

                                    {weeklySchedule[day].enabled && (
                                        <div className="flex flex-col space-y-2 ml-7">
                                            {weeklySchedule[day].slots.map((slot, idx) => (
                                                <div key={idx} className="flex flex-wrap items-center gap-2">
                                                    <span className="text-sm text-gray-500">From</span>
                                                    <select
                                                        value={slot.startTime}
                                                        onChange={(e) => updateSlot(day, idx, 'startTime', e.target.value)}
                                                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                                    >
                                                        {timeOptions.map((time) => (
                                                            <option key={time} value={time}>{time}</option>
                                                        ))}
                                                    </select>

                                                    <span className="text-sm text-gray-500">To</span>
                                                    <select
                                                        value={slot.endTime}
                                                        onChange={(e) => updateSlot(day, idx, 'endTime', e.target.value)}
                                                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                                    >
                                                        {timeOptions.map((time) => (
                                                            <option key={time} value={time}>{time}</option>
                                                        ))}
                                                    </select>

                                                    {weeklySchedule[day].slots.length > 1 && (
                                                        <button
                                                            onClick={() => removeSlot(day, idx)}
                                                            className="p-1 text-red-500 hover:text-red-700"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addSlot(day)}
                                                className="text-xs text-orange-500 hover:underline flex items-center"
                                            >
                                                <Plus className="h-3 w-3 mr-1" /> Add Slot
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                                <CalendarDays className="h-4 w-4 text-orange-500" /> Specific Dates
                            </h3>
                            <button
                                onClick={addSpecificDate}
                                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg flex items-center text-sm shadow-sm"
                            >
                                <Plus className="h-4 w-4 mr-1" /> Add Holiday
                            </button>
                        </div>

                        <div className="space-y-3">
                            {specificDates.map((specificDate) => (
                                <div key={specificDate.id} className="flex flex-wrap items-center gap-3 p-3 bg-orange-50 border border-orange-100 rounded-lg">
                                    <input
                                        type="date"
                                        value={specificDate.date}
                                        onChange={(e) => updateSpecificDate(specificDate.id, 'date', e.target.value)}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                    />

                                    <select
                                        value={specificDate.type}
                                        onChange={(e) => updateSpecificDate(specificDate.id, 'type', e.target.value)}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                    >
                                        <option value="unavailable">Unavailable</option>
                                        <option value="available">Available</option>
                                    </select>

                                    {specificDate.type === 'available' && (
                                        <>
                                            <select
                                                value={specificDate.startTime}
                                                onChange={(e) => updateSpecificDate(specificDate.id, 'startTime', e.target.value)}
                                                className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                            >
                                                {timeOptions.map((time) => (
                                                    <option key={time} value={time}>{time}</option>
                                                ))}
                                            </select>

                                            <select
                                                value={specificDate.endTime}
                                                onChange={(e) => updateSpecificDate(specificDate.id, 'endTime', e.target.value)}
                                                className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                            >
                                                {timeOptions.map((time) => (
                                                    <option key={time} value={time}>{time}</option>
                                                ))}
                                            </select>
                                        </>
                                    )}

                                    <button
                                        onClick={() => removeSpecificDate(specificDate.id)}
                                        className="p-1 text-red-500 hover:text-red-700"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex space-x-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center justify-center space-x-2 font-medium shadow-sm disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    <span>Save</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WeeklyAvailabilityModal;
